/**
 * "Best of" strip for the Gelbooru 0.2 sites that keep scores and have no date search (Gelbooru, Safebooru, Xbooru): the best posts of the
 * last day, week, month or year.
 *
 * Like Rule34 (lib/rule34-time-window.ts), these sites accept `id:>N` and ids grow with time, so a window is the id that was current N days
 * ago plus a score order: `id:>N sort:score:desc`. Checked live 2026-10-26: that query returned the top posts of the range on all three
 * (Gelbooru scores 61, 44, 40...). TBIB keeps no scores, so it has no such strip.
 *
 * The strip's choice reaches a source as `sourceOptions[site].bestOf` and becomes a marked `days:N` term; the source's own search turns it
 * into the id range (applyBestOfWindow in lib/booru-sources/booru-api/gelbooru.ts).
 */
// Derpibooru is in the list too (2026-10-27): its own date search takes the window (buildPhilomenaQuery in lib/booru-sources/booru-api/philomena.ts).
export const BEST_OF_SITES: readonly string[] = ["gelbooru", "safebooru", "xbooru", "derpibooru", "hypnohub"]

export const BEST_OF_WINDOWS = [
  { value: "day", days: 1 },
  { value: "week", days: 7 },
  { value: "month", days: 30 },
  { value: "year", days: 365 },
] as const

export type BestOf = (typeof BEST_OF_WINDOWS)[number]["value"]

export function isBestOf(value: unknown): value is BestOf {
  return typeof value === "string" && BEST_OF_WINDOWS.some((item) => item.value === value)
}

/**
 * Gelbooru's best posts of a month are 99% explicit (checked live 2026-10-26: 99 of 100), and a visitor's default Rating filter hides
 * explicit posts, so the strip showed "No posts found". The rating choice is therefore sent with the strip and Gelbooru is asked for only
 * those ratings (its ratings: general, sensitive, questionable, explicit; the app calls sensitive questionable). Safebooru is all-safe and
 * Xbooru's mix is fine, so only Gelbooru gets a term. `ratings` is the list of the ratings the viewer has on ("questionable_safe"; underscores,
 * because the search route's option cleaner keeps only letters, digits, _ : and -, so a comma would be dropped; commas are read too).
 */
export function ratingTermForBestOf(site: string, ratings: unknown): string {
  if (site !== "gelbooru" || typeof ratings !== "string") return ""
  const on = new Set(ratings.split(/[,_]/).map((r) => r.trim()).filter(Boolean))
  if (on.has("safe") && on.has("questionable") && on.has("explicit")) return ""
  if (on.has("safe") && on.has("questionable")) return "-rating:explicit"
  if (on.size === 1 && on.has("safe")) return "rating:general"
  if (on.has("questionable") && on.has("explicit") && !on.has("safe")) return "-rating:general"
  if (on.has("safe") && on.has("explicit") && !on.has("questionable")) return "-rating:questionable -rating:sensitive"
  return ""
}

/** `tags` for one source: unchanged for every source outside the list (and without a valid choice), otherwise the window and the score order
 * are added (and, for Gelbooru, the viewer's ratings). A search that already has its own order, sort or window keeps it (the viewer asked for that). */
export function withBestOfTerm(site: string, tags: string, bestOf: unknown, ratings?: unknown): string {
  if (!BEST_OF_SITES.includes(site) || !isBestOf(bestOf)) return tags
  if (/(^|[\s,])(order|sort|days|id):/i.test(tags)) return tags
  const days = BEST_OF_WINDOWS.find((item) => item.value === bestOf)!.days
  const rating = /(^|[\s,])-?rating:/i.test(tags) ? "" : ratingTermForBestOf(site, ratings)
  return [tags.trim(), `days:${days}`, "sort:score:desc", rating].filter(Boolean).join(" ")
}
